import { isEmpty, isLiteral } from "./expressionShapeUtils";
import {
  buildFuncEnumerations,
  buildFuncPairingType,
} from "./functionPairingUtils";
import { useCallback, useMemo } from "react";
import { useGetAvailableAggregatorsQuery } from "services/reportsV3Service";

export { isEmpty, isLiteral } from "./expressionShapeUtils";

export const useAggregatorData = () => {
  const { data } = useGetAvailableAggregatorsQuery();
  return useMemo(() => {
    const list = data?.LIST ?? [];
    const binary = data?.BINARY ?? [];
    const unary = data?.UNARY ?? [];
    const noParams = data?.NO_PARAMS ?? [];
    const column = data?.COLUMN ?? [];
    return {
      simpleFuncs: list.map((e) => e.func),
      pairFuncs: binary.map((e) => e.func),
      unaryFuncs: unary.map((e) => e.func),
      noParamsFuncs: noParams.map((e) => e.func),
      columnFuncs: column.map((e) => e.func),
      simpleDisplay: Object.fromEntries(
        list.map((e) => [e.func, e.displayName])
      ),
      pairDisplay: Object.fromEntries(binary.map((e) => [e.func, e.operator])),
      pairLabel: Object.fromEntries(binary.map((e) => [e.func, e.displayName])),
      unaryDisplay: Object.fromEntries(
        unary.map((e) => [e.func, e.displayName])
      ),
      noParamsDisplay: Object.fromEntries(
        noParams.map((e) => [e.func, e.displayName])
      ),
      columnDisplay: Object.fromEntries(
        column.map((e) => [e.func, e.displayName])
      ),
      funcValidFor: Object.fromEntries([
        ...list.map((e) => [e.func, e.validFor ?? []]),
        ...binary.map((e) => [e.func, e.validFor ?? []]),
        ...unary.map((e) => [e.func, e.validFor ?? []]),
        ...noParams.map((e) => [e.func, e.validFor ?? []]),
      ]),
      funcProduces: Object.fromEntries([
        ...list.map((e) => [e.func, e.produces ?? []]),
        ...binary.map((e) => [e.func, e.produces ?? []]),
        ...unary.map((e) => [e.func, e.produces ?? []]),
        ...noParams.map((e) => [e.func, e.produces ?? []]),
        ...column.map((e) => [e.func, e.produces ?? []]),
      ]),
      columnValidFor: Object.fromEntries(
        column.map((e) => [e.func, e.validFor ?? []])
      ),
      funcPairingType: buildFuncPairingType(data),
      funcEnumerations: buildFuncEnumerations(data),
    };
  }, [data]);
};

export const normalizeDataType = (dataType) => {
  if (dataType === "option") return "text";
  if (dataType === "year") return "unit";
  return dataType;
};

export const isExpressionValidWithData = (
  exp,
  {
    simpleFuncs = [],
    pairFuncs = [],
    unaryFuncs = [],
    noParamsFuncs = [],
    columnFuncs = [],
  }
) => {
  const agg = {
    simpleFuncs,
    pairFuncs,
    unaryFuncs,
    noParamsFuncs,
    columnFuncs,
  };
  const recurse = (e) => isExpressionValidWithData(e, agg);
  if (isEmpty(exp)) return false;
  if (isLiteral(exp)) return true;
  if (exp.func === "VAR" && exp.name) return true;
  if (columnFuncs.includes(exp.func)) return Boolean(exp.name);
  if (exp.func === "CASE")
    return (
      exp.cases?.length > 0 &&
      exp.cases.every((c) => recurse(c.when) && recurse(c.then)) &&
      recurse(exp.otherwise ?? {})
    );
  if (exp.func === "IN")
    return (
      recurse(exp.value) && exp.params?.length > 0 && exp.params.every(recurse)
    );
  if (simpleFuncs.includes(exp.func))
    return exp.values?.length > 0 && exp.values?.every(recurse);
  if (pairFuncs.includes(exp.func)) return recurse(exp.a) && recurse(exp.b);
  if (unaryFuncs.includes(exp.func)) return recurse(exp.value);
  return noParamsFuncs.includes(exp.func);
};

export const useIsExpressionValid = () => {
  const {
    simpleFuncs,
    pairFuncs,
    unaryFuncs,
    noParamsFuncs,
    columnFuncs,
  } = useAggregatorData();
  return useCallback(
    (exp) =>
      isExpressionValidWithData(exp, {
        simpleFuncs,
        pairFuncs,
        unaryFuncs,
        noParamsFuncs,
        columnFuncs,
      }),
    [simpleFuncs, pairFuncs, unaryFuncs, noParamsFuncs, columnFuncs]
  );
};

export function extractVarColumns(exp) {
  if (!exp || typeof exp !== "object" || Array.isArray(exp)) return [];
  if (exp.func === "VAR") return [exp.name];
  if (exp.func === "CASE")
    return [
      ...(exp.cases ?? []).flatMap((c) => [
        ...extractVarColumns(c.when),
        ...extractVarColumns(c.then),
      ]),
      ...extractVarColumns(exp.otherwise),
    ];
  if (exp.func === "IN")
    return [
      ...extractVarColumns(exp.value),
      ...(exp.params ?? []).flatMap(extractVarColumns),
    ];
  if ("values" in exp) return (exp.values ?? []).flatMap(extractVarColumns);
  if ("a" in exp && "b" in exp)
    return [...extractVarColumns(exp.a), ...extractVarColumns(exp.b)];
  if ("value" in exp) return extractVarColumns(exp.value);
  return [];
}
